import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "react-query";
import {
  fetchPartnerPortalTeamDirectory,
  fetchPlatformPortalTeamDirectory,
  listPartnerPortalTeamWorkspaces,
  listPlatformPortalTeamWorkspaces,
} from "../api/client";

const STALE_MS = 60_000;
const DEBOUNCE_MS = 3_000;

function workspacesQueryKey(mode) {
  return ["portalTeamWorkspaces", mode];
}

function sortWorkspaces(rows) {
  return [...rows].sort((a, b) =>
    String(a.workspaceLabel || a.workspaceSlug || "").localeCompare(
      String(b.workspaceLabel || b.workspaceSlug || ""),
      undefined,
      { sensitivity: "base" }
    )
  );
}

export function usePortalTeamDirectory(mode = "platform") {
  const debounceRef = useRef(null);
  const loadedSlugsRef = useRef(new Set());

  const fetchDirectory =
    mode === "partner" ? fetchPartnerPortalTeamDirectory : fetchPlatformPortalTeamDirectory;
  const listWorkspaces =
    mode === "partner" ? listPartnerPortalTeamWorkspaces : listPlatformPortalTeamWorkspaces;

  const workspacesQuery = useQuery(workspacesQueryKey(mode), listWorkspaces, {
    staleTime: STALE_MS,
    cacheTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const [workspaceDetails, setWorkspaceDetails] = useState({});
  const [loadingSlugs, setLoadingSlugs] = useState(() => new Set());

  const workspaceStubs = useMemo(() => {
    const rows = Array.isArray(workspacesQuery.data) ? workspacesQuery.data : [];
    return sortWorkspaces(
      rows.map((ws) => ({
        ...ws,
        conversations: [],
        activeUsers: [],
        activeCount: 0,
      }))
    );
  }, [workspacesQuery.data]);

  const directory = useMemo(() => {
    return workspaceStubs.map((stub) => workspaceDetails[stub.workspaceSlug] || stub);
  }, [workspaceStubs, workspaceDetails]);

  const patchDirectory = useCallback((updater) => {
    setWorkspaceDetails((prev) => {
      const merged = workspaceStubs.map((stub) => prev[stub.workspaceSlug] || stub);
      const next = typeof updater === "function" ? updater(merged) : updater;
      if (!Array.isArray(next)) return prev;

      const map = { ...prev };
      for (const ws of next) {
        const slug = ws?.workspaceSlug;
        if (!slug) continue;
        map[slug] = ws;
        loadedSlugsRef.current.add(slug);
      }
      return map;
    });
  }, [workspaceStubs]);

  const workspaceStubsRef = useRef(workspaceStubs);
  workspaceStubsRef.current = workspaceStubs;

  const loadWorkspace = useCallback(
    async (workspaceSlug, { force = false } = {}) => {
      const slug = String(workspaceSlug || "").trim();
      if (!slug) return null;

      if (!force && loadedSlugsRef.current.has(slug)) {
        return null;
      }

      setLoadingSlugs((prev) => new Set(prev).add(slug));
      try {
        const rows = await fetchDirectory(slug);
        const entry = Array.isArray(rows) ? rows[0] : null;
        const stub =
          workspaceStubsRef.current.find((w) => w.workspaceSlug === slug) || {
            workspaceSlug: slug,
            conversations: [],
            activeUsers: [],
            activeCount: 0,
          };
        const resolved = entry || {
          ...stub,
          conversations: [],
          activeUsers: [],
          activeCount: 0,
        };
        setWorkspaceDetails((prev) => ({ ...prev, [slug]: resolved }));
        loadedSlugsRef.current.add(slug);
        return resolved;
      } catch {
        return null;
      } finally {
        setLoadingSlugs((prev) => {
          const next = new Set(prev);
          next.delete(slug);
          return next;
        });
      }
    },
    [fetchDirectory]
  );

  const refreshWorkspace = useCallback(
    async (workspaceSlug) => loadWorkspace(workspaceSlug, { force: true }),
    [loadWorkspace]
  );

  const refreshDirectory = useCallback(async () => {
    await workspacesQuery.refetch();
    const slugs = [...loadedSlugsRef.current];
    await Promise.all(slugs.map((slug) => refreshWorkspace(slug)));
  }, [workspacesQuery, refreshWorkspace]);

  const scheduleDirectorySync = useCallback(
    (workspaceSlug) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = window.setTimeout(() => {
        debounceRef.current = null;
        const slug = String(workspaceSlug || "").trim();
        if (slug) void refreshWorkspace(slug);
        else void refreshDirectory();
      }, DEBOUNCE_MS);
    },
    [refreshDirectory, refreshWorkspace]
  );

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    []
  );

  const isWorkspaceLoading = useCallback(
    (slug) => loadingSlugs.has(String(slug || "").trim()),
    [loadingSlugs]
  );

  const isWorkspaceLoaded = useCallback(
    (slug) => {
      const s = String(slug || "").trim();
      return loadedSlugsRef.current.has(s) || Boolean(workspaceDetails[s]);
    },
    [workspaceDetails]
  );

  return {
    directory,
    isInitialLoading: workspacesQuery.isLoading && workspaceStubs.length === 0,
    isRefreshing: workspacesQuery.isFetching || loadingSlugs.size > 0,
    error: workspacesQuery.error,
    patchDirectory,
    refreshDirectory,
    refreshWorkspace,
    loadWorkspace,
    scheduleDirectorySync,
    isWorkspaceLoading,
    isWorkspaceLoaded,
  };
}
